import { COLOR } from '../../config/uiTokens'
import './DamageMotes.css'

// ─── Damage motes ─────────────────────────────────────────────────────────────
//
// The kill bar's cause made literal: a structure whose LAST footprint cell
// the fire sweep destroys lifts its yellow silhouette off the board — tagged
// "Structure Destroyed" — and flies it to the struck side's kill bar, where
// the next intact icon greys out as it lands. Plain land and partial damage
// fly nothing: cells aren't score. (The opponent replay's Base-build descent
// rides the same machinery, below.)
//
// Purely imperative DOM (like the legacy score particles): motes are fixed-
// position elements appended to <body>, flown with WAAPI so landing (onfinish)
// can tick the count. HexBoard schedules launches off the same fire-sweep
// delays that ignite the craters; cancelAllMotes() clears pending launches
// and in-flight motes on unmount.

// The silhouette rises slowly enough that the tag is readable in flight. (TUNE)
const STRUCT_MOTE_FLIGHT_MS = 1900

const pendingLaunches = new Set()   // timeout ids not yet fired
const liveMotes       = new Set()   // mote elements currently in flight

function schedule(delayMs, fn) {
    const id = setTimeout(() => { pendingLaunches.delete(id); fn() }, delayMs)
    pendingLaunches.add(id)
}

export function cancelAllMotes() {
    pendingLaunches.forEach(clearTimeout)
    pendingLaunches.clear()
    liveMotes.forEach(el => el.remove())
    liveMotes.clear()
}

const center = r => ({ x: r.left + r.width / 2, y: r.top + r.height / 2 })

// Flies `el` (already positioned at its start point) to the target element's
// center, then removes it and reports the landing.
function fly(el, from, toEl, opts) {
    flyToPoint(el, from, center(toEl.getBoundingClientRect()), opts)
}

// Same flight, but to a fixed viewport point (for targets that have no stable
// DOM element).
function flyToPoint(el, from, to, { durMs, endScale, endRotate, onLand,
                                    easing = 'cubic-bezier(0.5, 0, 0.75, 0.6)', endOpacity = 0.9 }) {
    document.body.appendChild(el)
    liveMotes.add(el)
    const frames = [
        { transform: 'translate(0, 0) scale(1) rotate(0deg)', opacity: 1 },
        { transform: `translate(${to.x - from.x}px, ${to.y - from.y}px) scale(${endScale}) rotate(${endRotate}deg)`,
          opacity: endOpacity },
    ]
    const anim = el.animate(frames, { duration: durMs, easing, fill: 'forwards' })
    anim.onfinish = () => {
        if (!liveMotes.has(el)) return   // cancelled mid-flight — don't tick the count
        liveMotes.delete(el)
        el.remove()
        onLand?.()
    }
}

// A corridor drone tile funding a Base build (the opponent replay's
// 2 → base-in-construction beat). The tile is CLONED SYNCHRONOUSLY — the
// caller invokes this while the funding tiles are still mounted, just before
// the replay board removes them — then the clone plays the send-off:
//   1. LIFT-OFF — a short in-place lift (a touch bigger, a pixel or two up:
//      wheels off the pad) with the first sway;
//   2. DESCENT  — the drop to its footprint cell, read as ALTITUDE rather
//      than travel (the pad is often only a hex or two away): the tile
//      shrinks steadily the whole way down to the Base drone's size, sways
//      gently like a hovering craft settling (a damped pendulum, no spin),
//      and its GROUND SHADOW — starting well below it, where the board casts
//      a high-flying drone's — slides along the ground to the pad and closes
//      under the tile as it lands. The whole tile moves as one, disc and
//      glyph alike;
//   3. DISSOLVE — it fades over the last stretch, melting into the
//      construction fading up beneath it.
// The hex tile positions itself in PERCENTAGES of the board (inline left /
// top / width / height); a clone keeping those inside the mote's own box
// would shrink to a sliver — so the clone's box is normalised to fill the
// mote before it plays.
const LIFT_SCALE     = 1.04
const LIFT_PX        = 2
const SWAY_DEG       = 6                  // the hover sway's first swing
const SWAY_CYCLES    = 1.25               // swings over the descent, damping to rest
const FLIGHT_SAMPLES = 24
export function launchBuildMote({ fromEl, toPoint, wiggleMs, flightMs, targetW, shadow = null, onLand }) {
    if (!fromEl) { onLand?.(); return }
    const r = fromEl.getBoundingClientRect()
    if (!r.width) { onLand?.(); return }
    const from = center(r)
    const el = document.createElement('span')
    el.className = 'dm__build'
    // Top-left anchored (no CSS centering transform — the flight animation
    // owns the transform property outright, so a class translate would be
    // dropped at takeoff and the mote would lurch by half a tile).
    el.style.cssText = `left:${from.x - r.width / 2}px; top:${from.y - r.height / 2}px;` +
                       `width:${r.width}px; height:${r.height}px;`
    // The ground shadow, its own element BENEATH the tile: sized and dropped
    // the way the board casts it (shadow: { dropPx, rx, ry } in px), so it
    // takes over from the board's the moment the tile is cloned.
    let shadowEl = null
    if (shadow && shadow.rx > 0) {
        shadowEl = document.createElement('span')
        shadowEl.className = 'dm__build-shadow'
        shadowEl.style.cssText = `left:${r.width / 2 - shadow.rx}px; top:${r.height / 2 - shadow.ry + shadow.dropPx}px;` +
                                 `width:${2 * shadow.rx}px; height:${2 * shadow.ry}px;`
        el.appendChild(shadowEl)
    }
    const clone = fromEl.cloneNode(true)
    clone.style.cssText = 'position:absolute; left:0; top:0; width:100%; height:100%; transform:none; transition:none; opacity:1; visibility:visible;'
    el.appendChild(clone)
    // Stage the clone NOW, pixel-perfect over the real tile: when the replay
    // board removes the tile a beat later, the clone is already covering it.
    document.body.appendChild(el)
    liveMotes.add(el)
    const durMs = wiggleMs + flightMs
    const wEnd  = wiggleMs / durMs               // lift-off → descent hand-off
    const at    = f => wEnd + f * (1 - wEnd)     // fraction of the descent
    const dx = toPoint.x - from.x
    const dy = toPoint.y - from.y
    const endScale = Math.max(0.2, (targetW ?? r.width * 0.4) / r.width)
    const T = (x, y, sc, deg) => `translate(${x}px, ${y}px) scale(${sc}) rotate(${deg}deg)`
    const tileFrames = [
        // 1 — lift-off in place.
        { transform: T(0, 0, 1, 0), opacity: 1, offset: 0, easing: 'ease-out' },
        { transform: T(0, -LIFT_PX, LIFT_SCALE, SWAY_DEG * 0.5), opacity: 1, offset: wEnd, easing: 'linear' },
    ]
    const shadowFrames = shadowEl ? [
        { transform: 'translate(0, 0) scale(1)', opacity: 1, offset: 0, easing: 'linear' },
        { transform: 'translate(0, 0) scale(1)', opacity: 1, offset: wEnd, easing: 'linear' },
    ] : null
    // 2 + 3 — the descent, sampled densely (each interval linear).
    for (let i = 1; i <= FLIGHT_SAMPLES; i++) {
        const t = i / FLIGHT_SAMPLES
        const p = t * t * (3 - 2 * t)                              // along the line: ease in, ease out (a settle)
        const alt = 1 - p                                           // altitude left: 1 up high → 0 on the pad
        const sc = endScale + (LIFT_SCALE - endScale) * alt         // steady shrink the whole way down
        const deg = SWAY_DEG * Math.cos(2 * Math.PI * SWAY_CYCLES * t) * alt   // damped hover sway, at rest on landing
        const opacity = t < 0.8 ? 1 : Math.max(0, (1 - t) / 0.2)
        tileFrames.push({ transform: T(dx * p, dy * p - LIFT_PX * alt, sc, deg), opacity, offset: at(t), easing: 'linear' })
        if (shadowFrames) {
            // The shadow travels on the ground straight to the pad — the gap
            // between it and the tile (the drop) closes as the tile comes down.
            shadowFrames.push({
                transform: `translate(${dx * p}px, ${(dy - shadow.dropPx) * p}px) scale(${sc})`,
                opacity, offset: at(t), easing: 'linear',
            })
        }
    }
    const anim = clone.animate(tileFrames, { duration: durMs, easing: 'linear', fill: 'forwards' })
    if (shadowEl) shadowEl.animate(shadowFrames, { duration: durMs, easing: 'linear', fill: 'forwards' })
    anim.onfinish = () => {
        if (!liveMotes.has(el)) return   // cancelled mid-flight
        liveMotes.delete(el)
        el.remove()
        onLand?.()
    }
}

// Silhouette of a fully destroyed structure — one HEX per footprint cell,
// each a flat-top hexagon drawn into the cell's actual on-screen box so the
// shape lifts straight off the board — riding with a "Structure Destroyed"
// tag to the kill-bar icon it will grey out. It wears its side's structure
// paint (STRUCT_PAINT): yours the teal your buildings and kill-bar icons
// wear, the enemy's the structure yellow of the intel that found it.
// A flat-top hex fills its bounding box corner to corner: points at the
// box's left and right mid-height, the flat edges a quarter of the way in.
const HEX_CELL_POINTS = '100,50 75,100 25,100 0,50 25,0 75,0'
export const STRUCT_PAINT = {
    self: { light: COLOR.STRUCT_SELF, dark: COLOR.STRUCT_SELF_DARK, stroke: 'rgba(0, 60, 55, 0.85)' },
    opp:  { light: '#ffd84a', dark: '#e8a90e', stroke: 'rgba(90, 60, 0, 0.85)' },
}
const hexCellSvg = (paint, id) =>
    '<svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">' +
    `<defs><linearGradient id="${id}" x1="0" y1="0" x2="1" y2="1">` +
    `<stop offset="0" stop-color="${paint.light}"/><stop offset="1" stop-color="${paint.dark}"/></linearGradient></defs>` +
    `<polygon points="${HEX_CELL_POINTS}" fill="url(#${id})" stroke="${paint.stroke}" stroke-width="1.5" vector-effect="non-scaling-stroke"/>` +
    '</svg>'
export function launchStructureMote({ delayMs, getFromEls, getToEl, label = 'Structure Destroyed', side = 'opp', onLand }) {
    const paint = STRUCT_PAINT[side] ?? STRUCT_PAINT.opp
    schedule(delayMs, () => {
        const fromEls = getFromEls()
        const toEl    = getToEl()
        if (!fromEls.length || !toEl) { onLand?.(); return }
        const rects = fromEls.map(e => e.getBoundingClientRect())
        const bbox = {
            left:   Math.min(...rects.map(r => r.left)),
            top:    Math.min(...rects.map(r => r.top)),
            right:  Math.max(...rects.map(r => r.right)),
            bottom: Math.max(...rects.map(r => r.bottom)),
        }
        const from = { x: (bbox.left + bbox.right) / 2, y: (bbox.top + bbox.bottom) / 2 }
        const el = document.createElement('span')
        el.className = 'dm__struct'
        el.dataset.side = side
        el.style.cssText = `left:${from.x}px; top:${from.y}px;` +
                           `width:${bbox.right - bbox.left}px; height:${bbox.bottom - bbox.top}px;`
        for (const r of rects) {
            const hex = document.createElement('span')
            hex.className = 'dm__struct-cell'
            hex.style.cssText = `left:${r.left - bbox.left}px; top:${r.top - bbox.top}px;` +
                                `width:${r.width}px; height:${r.height}px;`
            hex.innerHTML = hexCellSvg(paint, `dm-hex-grad-${side}`)
            el.appendChild(hex)
        }
        const tag = document.createElement('span')
        tag.className = 'dm__struct-tag'
        tag.textContent = label
        tag.style.color = paint.light
        el.appendChild(tag)
        fly(el, from, toEl, { durMs: STRUCT_MOTE_FLIGHT_MS, endScale: 0.4, endRotate: 0, onLand })
    })
}
